import React, { useState, useContext, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import debounce from 'lodash/debounce';
import api from '../utils/api';
import { FirebaseContext } from '../contexts/Firebase';
import { ChatContext } from '../contexts/Chat';
import { UserContext } from '../contexts/User';
import ReactMarkdown from 'react-markdown';
import '../styles/Chat.css';
import { FaHistory, FaCog, FaPlus, FaTrash, FaPaperclip, FaStop, FaMicrophone } from 'react-icons/fa';
import { BsCardText } from "react-icons/bs";
import { doc, collection } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import useTextSelection from '../components/useTextSelection';
import { providers, getChatProviders } from '../components/util';
import { getRandomAnimationData, LottieAnimation } from '../components/LottieAnimations';
import playTextToSpeech from '../components/TextToSpeech';
import VoiceRecorder from '../components/VoiceRecorder';
import { isImageGenerationRequest, extractImagePrompt } from '../utils/imageDetection';
import { isVideoGenerationRequest, extractVideoPrompt } from '../utils/videoDetection';
import SystemContextModal from '../components/SystemContextModal';
import MessageRenderer from '../components/MessageRenderer';
import logger from '../utils/logger';

const Chat = () => {
  const { db, storage } = useContext(FirebaseContext);
  const {
    user,
    totalCost,
    defaultSystemContext,
    tier,
    costLimit,
    videoGenCount,
    videoGenLimit,
    imageGenCount,
    imageGenLimit,
    checkQuotaLimit,
    incrementVideoGenCount,
    incrementImageGenCount
  } = useContext(UserContext);
  const {
    sessionTitle,
    setSessionTitle,
    contextWindow,
    setContextWindow,
    addInteraction,
    chatId,
    setChatId,
    saveChatSession,
    apiContext,
    setApiContext,
    deleteInteraction,
    totalTokens,
    setTotalTokens,
    totalCost: sessionTotalCost,
    setTotalCost: setSessionTotalCost,
    setHighestInteractionId,
    checkAndSummarizeApiContext,
    isSummarizing,
    isSessionTitleManuallyUpdated,
    setIsSessionTitleManuallyUpdated,
    session_title_summary,
    setSessionTitleSummary,
    modelMetadata
  } = useContext(ChatContext);

  const navigate = useNavigate();
  const voiceRecorderRef = useRef(null);
  const [isRecording, setIsRecording] = useState(false);
  const [respondWithAudio, setRespondWithAudio] = useState(true);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [isSendButtonDisabled, setIsSendButtonDisabled] = useState(false);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [currentQuery, setCurrentQuery] = useState('');
  const [selectedModel, setSelectedModel] = useState('xai'); // Default to cheapest (Grok)
  const [chatProviders, setChatProviders] = useState(providers); // Dynamic provider list
  const [files, setFiles] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const [isSystemContextModalOpen, setIsSystemContextModalOpen] = useState(false);

  // Check if any limit is reached
  const isAnyLimitReached = totalCost >= costLimit ||
                            imageGenCount >= imageGenLimit ||
                            videoGenCount >= videoGenLimit;

  const openSystemContextModal = () => setIsSystemContextModalOpen(true);
  const closeSystemContextModal = () => setIsSystemContextModalOpen(false);

  // Update chat providers when model metadata loads
  useEffect(() => {
    if (modelMetadata && Object.keys(modelMetadata).length > 0) {
      const dynamicProviders = getChatProviders(modelMetadata);
      setChatProviders(dynamicProviders);
      logger.log('Chat providers updated:', dynamicProviders);
    }
  }, [modelMetadata]);

  useEffect(() => {
    if (!chatId && user) {
      const newChatRef = doc(collection(doc(db, 'users', user.uid), 'chats'));
      setChatId(newChatRef.id);
    }
  }, [db, chatId, setChatId, user]);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [contextWindow]);

  useEffect(() => {
    if (!isSessionTitleManuallyUpdated) {
      setSessionTitle(session_title_summary);
    }
  }, [session_title_summary, isSessionTitleManuallyUpdated]);
  
  useEffect(() => {
    if (!isSessionTitleManuallyUpdated && sessionTitle !== 'New Chat Session') {
      saveChatSession();
    }
  }, [sessionTitle, isSessionTitleManuallyUpdated]);

  const handleWordSelectionComplete = async (selectedText, type) => {
    logger.log(`${type.charAt(0).toUpperCase() + type.slice(1)} selected:`, selectedText);
    logger.log("lets open a modal/ tool tip");
  };

  const handleSentenceSelectionComplete = async (selectedText, type) => {
    logger.log(`${type.charAt(0).toUpperCase() + type.slice(1)} selected:`, selectedText);
    await playTextToSpeech(selectedText, 'random');
  };
      
  useTextSelection(".message-group", handleWordSelectionComplete, handleSentenceSelectionComplete);

  const handleStartRecording = () => {
    voiceRecorderRef.current.startRecording();
    setIsRecording(true);
  };

  const handleStopRecording = async () => {
    setIsTranscribing(true);
  
    try {
      await voiceRecorderRef.current.stopRecording();
      const transcription = await voiceRecorderRef.current.getTranscription();
      if (transcription) {
        handleSendQuery(transcription, true);
      } else {
        setIsTranscribing(false);  // Reset transcribing state here if no transcription
      }
    } catch (error) {
      logger.error("Error stopping recording and getting transcription:", error);
      setIsTranscribing(false);  // Reset transcribing state here in case of error
    }
  };

  const handleTitleClick = () => {
    setIsEditingTitle(true);
  };

  const handleTitleChange = (e) => {
    setSessionTitle(e.target.value);
  };

  const handleTitleBlur = async () => {
    setIsEditingTitle(false);
    setIsSessionTitleManuallyUpdated(true);
    await saveChatSession();
  };

  const handleDeleteInteraction = async (interactionId) => {
    await deleteInteraction(interactionId);
  };

  const handleNewChat = () => {
    const newChatRef = doc(collection(doc(db, 'users', user.uid), 'chats'));
    setChatId(newChatRef.id);
    setSessionTitle('New Chat Session');
    setContextWindow([]);
    setTotalTokens(0);
    setApiContext([]);
    setHighestInteractionId(0);
  };

  const groupMessages = () => {
    const groups = [];
    let currentGroup = [];

    contextWindow.forEach((message, index) => {
      currentGroup.push(message);
      if (message.role === 'assistant' || index === contextWindow.length - 1) {
        groups.push(currentGroup);
        currentGroup = [];
      }
    });

    return groups.filter(group => group.length > 0);
  };

  const uploadFilesAndGetUrls = async (files, userId, storage) => {
    if (!Array.isArray(files)) files = Array.from(files);
    const fileUrls = await Promise.all(files.map(async (file) => {
      const storageRef = ref(storage, `${userId}/${file.name}`);
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);
      return {
        type: "image_url",
        image_url: { url },
        file
      };
    }));
    return fileUrls;
  };

  const handleFileDrop = async (droppedFiles) => {
    const newFiles = Array.from(droppedFiles);
    setFiles((prevFiles) => [...prevFiles, ...newFiles]);
    await handleFileUpload(newFiles);
    setDragOver(false);
  };

  const handleFileUpload = async (files) => {
    const uploadedFileUrls = await uploadFilesAndGetUrls(files, user.uid, storage);
    setUploadedFiles((prevUrls) => [...prevUrls, ...uploadedFileUrls]);
  };

  const handleFileChange = async (e) => {
    const newFiles = Array.from(e.target.files);
    setFiles((prevFiles) => [...prevFiles, ...newFiles]);
    await handleFileUpload(newFiles);
  };

  const handleRemoveFile = (index) => {
    setFiles(prevFiles => prevFiles.filter((_, i) => i !== index));
    setUploadedFiles(prevUrls => prevUrls.filter((_, i) => i !== index));
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendQuery();
    }
  };

  const handleQueryChange = (e) => {
    setCurrentQuery(e.target.value);
  };

  const handleModelChange = (e) => {
    setSelectedModel(e.target.value);
  };

  const handleButtonClick = () => {
    if (!isSendButtonDisabled) {
      setIsSendButtonDisabled(true);
      handleSendQuery();
      // debouncedHandleSendQuery();
    }
  };

  const debouncedHandleSendQuery = useCallback(
    debounce(() => {
      handleSendQuery();
    }, 300),
    []
  );

  const handleSendQuery = async (manualQuery = null, wasAudioInput = false) => {
    if (isAnyLimitReached) {
      return;
    }
    
    const queryToSend = manualQuery !== null ? manualQuery : currentQuery.trim();
    if (!queryToSend && files.length === 0) {
      toast.error("Please type your message or attach a file first");
      return;
    }

    try {
      setIsLoading(true);
      setIsRecording(false);
      setIsTranscribing(false); 

      // 🎨 AUTO-DETECT IMAGE GENERATION REQUEST
      const isImageRequest = isImageGenerationRequest(queryToSend);
      const isVideoRequest = isVideoGenerationRequest(queryToSend);

      if (isVideoRequest && uploadedFiles.length === 0) {
        // 🎬 VIDEO GENERATION MODE

        // CHECK QUOTA FIRST
        if (videoGenCount >= videoGenLimit) {
          toast.error(`Video generation limit reached (${videoGenLimit}). Please upgrade to continue.`);
          setIsLoading(false);
          setIsSendButtonDisabled(false);
          return;
        }

        const videoPrompt = extractVideoPrompt(queryToSend);
        logger.log("🎬 Video generation detected! Prompt:", videoPrompt);

        // Add user message to context
        const userMessage = {
          role: 'user',
          content: [{ type: "text", text: queryToSend }],
          temp: 'butthead'
        };
        setApiContext([...apiContext, userMessage]);
        setContextWindow([...contextWindow, userMessage]);
        setCurrentQuery('');

        // Show loading animation
        const placeholderMessage = {
          role: 'assistant',
          content: { type: 'animation', animationData: getRandomAnimationData() },
          temp: 'placeholder'
        };
        setContextWindow(prev => [...prev, placeholderMessage]);

        try {
          // Generate video using Veo3 (Gemini)
          const videoResponse = await api.post('/callAI', {
            provider: 'gemini',  // Veo3 is Gemini
            prompt: videoPrompt,
            task: 'video',
            metadata: {
              model: 'veo-3.1-generate-preview',  // Latest Veo3.1
              aspect_ratio: '16:9',
              // duration_seconds omitted - let API use default
              firebase_auth_id: user?.uid,  // Required for Firebase Storage upload
              project_id: 'dreamifai'
            }
          }, {
            headers: { 'Content-Type': 'application/json' },
            timeout: 120000 // 120s for video generation (slower than image)
          });

          logger.log("🎬 Video generation response:", videoResponse.data);

          // Extract video URL
          const videoUrl = videoResponse.data.assetUrl || videoResponse.data.public_urls?.[0] || videoResponse.data.urls?.[0];

          if (videoUrl) {
            const assistantContent = [
              { type: "text", text: `Generated video: ${videoPrompt}` },
              { type: "video_url", video_url: { url: videoUrl } }
            ];

            const assistantMessage = {
              role: 'assistant',
              content: assistantContent,
              inputTokens: 0,  // Video gen doesn't use input tokens
              outputTokens: 0,
              model: 'veo-3.1-generate-preview',
              temp: 'butthead'
            };

            setApiContext(prev => [...prev, assistantMessage]);
            setContextWindow(prev => {
              const filtered = prev.filter(m => m.temp !== 'placeholder');
              return [...filtered, assistantMessage];
            });

            await addInteraction(assistantMessage);

            // AFTER SUCCESS: increment counter
            await incrementVideoGenCount();
          } else {
            throw new Error("Video URL not found in response");
          }
        } catch (videoError) {
          logger.error("🎬 Video generation failed:", videoError);
          const errorMessage = {
            role: 'assistant',
            content: [{ type: "text", text: `Sorry, video generation failed: ${videoError.response?.data?.message || videoError.message}` }],
            inputTokens: 0,
            outputTokens: 0,
            model: 'error',
            temp: 'butthead'
          };
          setContextWindow(prev => {
            const filtered = prev.filter(m => m.temp !== 'placeholder');
            return [...filtered, errorMessage];
          });
        } finally {
          setIsLoading(false);
          setIsSendButtonDisabled(false);
        }
        return; // Exit early - video generated
      }

      if (isImageRequest && uploadedFiles.length === 0) {
        // CHECK QUOTA FIRST
        if (imageGenCount >= imageGenLimit) {
          toast.error(`Image generation limit reached (${imageGenLimit}). Please upgrade to continue.`);
          setIsLoading(false);
          setIsSendButtonDisabled(false);
          return;
        }

        // Extract clean prompt and generate image immediately
        const imagePrompt = extractImagePrompt(queryToSend);
        logger.log("🎨 Image generation detected! Prompt:", imagePrompt);

        // Add user message to context
        const userMessage = {
          role: 'user',
          content: [{ type: "text", text: queryToSend }],
          temp: 'butthead'
        };
        setApiContext([...apiContext, userMessage]);
        setContextWindow([...contextWindow, userMessage]);
        setCurrentQuery('');

        // Show loading animation
        const placeholderMessage = {
          role: 'assistant',
          content: { type: 'animation', animationData: getRandomAnimationData() },
          temp: 'placeholder'
        };
        setContextWindow(prev => [...prev, placeholderMessage]);

        try {
          // Generate image using Gemini Imagen (nano-banana-pro-preview)
          const imageResponse = await api.post('/callAI', {
            provider: 'gemini',  // Use Gemini for image gen (cheapest + best quality)
            prompt: imagePrompt,
            task: 'image',
            metadata: {
              model: 'nano-banana-pro-preview',
              n: 1,
              firebase_auth_id: user?.uid,  // Required for Firebase Storage upload
              project_id: 'dreamifai'
            }
          }, {
            headers: { 'Content-Type': 'application/json' },
            timeout: 60000 // 60s for image generation
          });

          logger.log("🎨 Image generation response:", imageResponse.data);

          // Extract image URL
          const imageUrl = imageResponse.data.assetUrl || imageResponse.data.public_urls?.[0] || imageResponse.data.urls?.[0];

          if (imageUrl) {
            const assistantContent = [
              { type: "text", text: `Generated: ${imagePrompt}` },
              { type: "image_url", image_url: { url: imageUrl } }
            ];

            const assistantMessage = {
              role: 'assistant',
              content: assistantContent,
              inputTokens: 0,  // Image gen doesn't use input tokens
              outputTokens: 0, // No text tokens
              model: 'nano-banana-pro-preview',
              temp: 'butthead'
            };

            setApiContext(prev => [...prev, assistantMessage]);
            setContextWindow(prev => {
              const filtered = prev.filter(m => m.temp !== 'placeholder');
              return [...filtered, assistantMessage];
            });

            await addInteraction(assistantMessage);

            // AFTER SUCCESS: increment counter
            await incrementImageGenCount();
          } else {
            throw new Error("Image URL not found in response");
          }
        } catch (imageError) {
          logger.error("🎨 Image generation failed:", imageError);
          const errorMessage = {
            role: 'assistant',
            content: [{ type: "text", text: `Sorry, image generation failed: ${imageError.response?.data?.message || imageError.message}` }],
            inputTokens: 0,
            outputTokens: 0,
            model: 'error',
            temp: 'butthead'
          };
          setContextWindow(prev => {
            const filtered = prev.filter(m => m.temp !== 'placeholder');
            return [...filtered, errorMessage];
          });
        } finally {
          setIsLoading(false);
          setIsSendButtonDisabled(false);
        }
        return; // Exit early - image generated
      }

      // 💬 NORMAL CHAT MODE (not an image request)
      let contentStructure = [{ type: "text", text: queryToSend }];

      if (uploadedFiles.length > 0) {
        contentStructure = contentStructure.concat(uploadedFiles.map(fileObj => ({
          type: "image_url",
          image_url: { url: fileObj.image_url.url }
        })));
      }

      const userMessage = { role: 'user', content: contentStructure, temp: 'butthead' };
      const newApiContext = [...apiContext, userMessage];
      setApiContext(newApiContext);
      setContextWindow([...contextWindow, userMessage]);
      setCurrentQuery('');

      let contextForApi = [...newApiContext];
      if (contextForApi.length > 0 && contextForApi[0].role !== 'user') {
        contextForApi.unshift({ role: 'user', content: contextForApi.shift().content });
      }

      const placeholderMessage = {
        role: 'assistant',
        content: { type: 'animation', animationData: getRandomAnimationData() },
        temp: 'placeholder'
      };
      setContextWindow(prev => [...prev, placeholderMessage]);

      // ai_backend handles image format conversion for all providers

      // Build system context
      if (defaultSystemContext) {  // Use defaultSystemContext from UserContext
        const systemMessageIndex = contextForApi.findIndex(msg => msg.role === 'system');
        if (systemMessageIndex === -1) {
          // If there's no system message, add it at the beginning
          contextForApi.unshift({ role: 'system', content: defaultSystemContext.context });
        } else {
          // If there's already a system message, append to its content
          contextForApi[systemMessageIndex].content += '\n' + defaultSystemContext.context;
        }
      }

      logger.log('apiContext to API endpoint:', contextForApi);

      setFiles([]);
      setUploadedFiles([]);

      // Clean the context: remove ALL frontend metadata before sending to backend
      const cleanedContext = contextForApi.map(msg => {
        const { temp, id, inputTokens, outputTokens, inputCost, outputCost, model, ...cleanMsg } = msg;
        return cleanMsg;
      });

      const response = await api.post('/callAI', {
        provider: selectedModel,
        prompt: cleanedContext,
        task: null,
        metadata: {}
      }, {
        headers: {
          'Content-Type': 'application/json',
        },
        timeout: 30000 // 30 seconds timeout
      });

      logger.log("raw response", response);
      let assistantContent = response.data.text || response.data.result?.text;
      let inputTokens = response.data.usage?.input_tokens || response.data.usage?.prompt_tokens || 0;
      let outputTokens = response.data.usage?.output_tokens || response.data.usage?.completion_tokens || response.data.usage?.response_tokens || 0;
      const modelUsed = response.data.model || selectedModel;

      const assistantMessage = { role: 'assistant', content: assistantContent, inputTokens, outputTokens, model: modelUsed, temp: 'butthead' };
      const newTitleSummary = response.data?.session_title_summary ?? sessionTitle;
      setSessionTitleSummary(newTitleSummary);

      setContextWindow(prev => {
        const newContext = [...prev];
        const placeholderIndex = newContext.findIndex(msg => msg.temp === 'placeholder');
        newContext[placeholderIndex] = assistantMessage;
        return newContext;
      });

      setApiContext(prev => {
        const updatedApiContext = [...prev, assistantMessage];

        let totalTokens = inputTokens + outputTokens;
        checkAndSummarizeApiContext(totalTokens, updatedApiContext, contextWindow);

        return updatedApiContext;
      });
      
      const interaction = {
        inputTokens,
        outputTokens,
        model: modelUsed,
        user_content: contentStructure,
        assistant_content: assistantContent
      };

      await addInteraction(interaction);

      if (wasAudioInput && respondWithAudio) {
        await playTextToSpeech(assistantContent, 'random');
      }

      setIsLoading(false);
      setIsSendButtonDisabled(false); 
      setIsRecording(false);
      setIsTranscribing(false);
    } catch (error) {
      logger.error('Error:', error);

      // Reset state
      setContextWindow(prev => prev.filter(msg => msg.temp !== 'placeholder'));
      setApiContext(prev => prev.filter(msg => msg.temp !== 'butthead'));
      setCurrentQuery(queryToSend);

      // User-friendly error messages
      let errorMessage = 'Failed to get response. ';
      if (error.response) {
        const status = error.response.status;
        if (status === 400) errorMessage += 'Invalid request format.';
        else if (status === 401) errorMessage += 'Authentication failed.';
        else if (status === 429) errorMessage += 'Rate limit exceeded. Please wait.';
        else if (status === 500) errorMessage += 'Server error. Please try again.';
        else if (status === 503) errorMessage += 'Service unavailable. Try again later.';
        else errorMessage += error.response.data?.message || error.message;
      } else if (error.code === 'ECONNABORTED') {
        errorMessage += 'Request timed out.';
      } else {
        errorMessage += 'Network error. Check your connection.';
      }

      toast.error(errorMessage, { autoClose: 5000 });
      setIsLoading(false);
      setIsRecording(false);
      setIsTranscribing(false);
      setIsSendButtonDisabled(false);
    }
  };

  return (
    <div className="chat-container">
      <div className="chat-header">
        <div className={`chat-nav`}>
          <FaPlus className="new-chat-icon" onClick={handleNewChat} />
          <BsCardText className="context-icon" onClick={openSystemContextModal} />
          <FaHistory className="history-icon" onClick={() => navigate('/history')} />
          <FaCog className="settings-icon" onClick={() => navigate('/settings')} />
        </div>
        {isEditingTitle ? (
          <input
            type="text"
            value={sessionTitle}
            onChange={handleTitleChange}
            onBlur={handleTitleBlur}
            autoFocus
          />
        ) : (
          <h2 onClick={handleTitleClick}>{sessionTitle}</h2>
        )}
        <div className="header-metrics">
          <span className="total-cost">Session: ${sessionTotalCost.toFixed(4)}</span>
          <span className="total-tokens">Tokens: {totalTokens.toLocaleString()}</span>
        </div>
      </div>
      <div className="chat-messages">
        {groupMessages().map((group, index) => (
          <div key={index} className="message-group">
            {group.map((item, i) => (
              <div key={i} className={`message ${item.role === 'user' ? 'user-message' : 'ai-message'}`}>
                <div className="message-content">
                  {item.content && item.content.type === 'animation' ? (
                    <div className="message-container loading_animation">
                      <LottieAnimation animationData={item.content.animationData} />
                    </div>
                  ) : (
                    <MessageRenderer
                      content={item.content}
                      isUser={item.role === 'user'}
                    />
                  )}
                </div>
                {item.role === 'assistant' && (
                  <div className="message-meta">
                    <div className="meta-info">
                      <span>Tokens: {(item.inputTokens || 0) + (item.outputTokens || 0)}</span>
                      {item.inputCost !== undefined && item.outputCost !== undefined && (
                        <span>Cost: ${((item.inputCost || 0) + (item.outputCost || 0)).toFixed(5)}</span>
                      )}
                      <span>Model: {item.model}</span>
                    </div>
                    <FaTrash className="delete-icon" onClick={() => handleDeleteInteraction(item.id)} />
                  </div>
                )}
              </div>
            ))}
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>
      <div className="chat-prompt">
        <div
          className={`input-container ${dragOver ? 'drag-over' : ''}`}
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            handleFileDrop(e.dataTransfer.files);
          }}
        >
          <div className="icons-container">
            <label htmlFor="fileInput" className="file-input-label">
              <FaPaperclip />
            </label>
            <input
              type="file"
              id="fileInput"
              multiple
              style={{ display: 'none' }}
              onChange={handleFileChange}
              disabled={isAnyLimitReached || isLoading || isSummarizing || isRecording}
            />
            <button 
              className={`recording-button ${isRecording ? 'stop' : ''} ${isTranscribing ? 'transcribing' : ''}`}
              onMouseDown={(e) => {
                e.preventDefault();
                if (!isTranscribing) {
                  isRecording ? handleStopRecording() : handleStartRecording();
                }
              }}
            >{isRecording ? <FaStop /> : <FaMicrophone />}</button>
            <VoiceRecorder ref={voiceRecorderRef} 
              showUI={false} 
              setIsRecording={setIsRecording}
              setIsTranscribing={setIsTranscribing}
              isRecording={isRecording}
              handleStopRecording={handleStopRecording} 
            />
          </div>
          
          <div className="input-wrapper">
            <div className="file-previews">
              {files.map((file, index) => (
                <div key={index} className="file-preview">
                  <img src={URL.createObjectURL(file)} alt="Preview" title={file.name} />
                  <button className="remove-btn" onClick={() => handleRemoveFile(index)}>x</button>
                </div>
              ))}
            </div>
            <textarea
              placeholder={
                isAnyLimitReached
                  ? `Usage limit reached. Please ${user?.isAnonymous ? 'upgrade to Plus tier' : 'check Settings for your quota usage'}.`
                  : isLoading
                    ? 'Waiting for response...'
                    : isSummarizing
                      ? 'Updating context, please wait...'
                      : isRecording
                        ? 'Recording in progress...'
                        : 'Type your message...'
              }
              rows={3}
              value={currentQuery}
              onChange={handleQueryChange}
              onKeyDown={handleKeyDown}
              disabled={isAnyLimitReached || isLoading || isSummarizing || isRecording}
            />
          </div>
        </div>

        <div className="checkbox-container">
          <input
            type="checkbox"
            id="respondWithAudio"
            checked={respondWithAudio}
            onChange={() => setRespondWithAudio(!respondWithAudio)}
          />
          <label htmlFor="respondWithAudio">Respond with audio to voice input</label>
        </div>

        <div className="chat-controls">
          <select value={selectedModel} onChange={handleModelChange} disabled={isLoading || isSummarizing || isRecording}>
            {Object.keys(chatProviders).map((key) => (
              <option key={key} value={key}>{chatProviders[key]}</option>
            ))}
          </select>
          <button
            onClick={handleButtonClick}
            onTouchStart={handleButtonClick}
            disabled={isAnyLimitReached || isLoading || isSummarizing || isRecording}
          >
            Send
          </button>
        </div>
      </div>
      <ToastContainer position="bottom-center" />
      <SystemContextModal 
        isOpen={isSystemContextModalOpen} 
        onClose={closeSystemContextModal} 
      />
    </div>
  );
};

export default Chat;
